import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { ButtonDirective } from 'primeng/button';
import { ContentItemCardComponent } from '../content-item-card/content-item-card';
import { JsonData } from '../../models';
import { deriveSlug, generateEntityId, makeUniqueSlug } from '../../utils/slug';
import { humanizeEnum } from '../../utils/format';
import { AeComp } from '../../utils/ae-converter';

@Component({
	selector: 'app-content-items-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, ButtonDirective, ContentItemCardComponent],
	templateUrl: './content-items-form.html',
	styleUrl: './content-items-form.scss',
})
export class ContentItemsFormComponent {
	content = input<JsonData[]>([]);
	aeComp = input<AeComp>({ fps: 25, width: 1920, height: 1080 });
	contentChange = output<JsonData[]>();
	copyItem = output<JsonData>();

	protected newType = signal('TEXT');

	protected readonly typeOptions = [
		'TEXT', 'IMAGE', 'VIDEO', 'WEBCAM', 'BOX', 'ADDRESS', 'DATETIME', 'COUNTDOWN', 'CALENDAR', 'SCREENSHOT',
	].map(value => ({ label: humanizeEnum(value), value }));

	private defaults: Record<string, JsonData> = {
		TEXT: {
			text: '',
			fontFamily: 'Arial',
			fontSize: 100,
			color: '#000000',
			align: 'left',
			fontWeight: 'normal',
			fontStyle: 'normal',
			wordWrap: false,
			uppercase: false,
			prefix: '',
			postfix: '',
			letterSpacing: 0,
			lineHeight: 1,
			removeDiacritics: false,
		},
		IMAGE: { src: '' },
		VIDEO: { src: '', autoPlay: false, loop: false },
		WEBCAM: { mode: 'PHOTO' },
	};

	protected addItem(dataType: string): void {
		const existing = this.content();
		const taken = new Set<string>(existing.map((i: JsonData) => (i as { slug?: string }).slug).filter((s: string | undefined): s is string => !!s));
		const base = dataType.toLowerCase() || 'item';
		const slug = makeUniqueSlug(deriveSlug(base), taken);
		this.contentChange.emit([
			...existing,
			{
				id: generateEntityId(),
				name: '',
				slug,
				source: { dataType, data: this.defaults[dataType] ?? {} },
				// Without fitTo the player stretches content across the whole
				// quad, which deforms text glyphs.
				placement: {
					type: 'PERSPECTIVE_TRANSFORM',
					// DEV-701: hPosition must mirror the default align ('left') —
					// the player positions the text block via hPosition, not align.
					...(dataType === 'TEXT' ? { fitTo: 'size', hPosition: 'LEFT' } : {}),
					fixedSize: { width: 200, height: 50 },
				},
				blending: [],
				filters: [],
				masked: false,
			},
		]);
	}

	protected updateItem(index: number, updated: JsonData): void {
		const clone = [...this.content()];
		clone[index] = updated;
		this.contentChange.emit(clone);
	}

	protected removeItem(index: number): void {
		if (!confirm('Delete this content item?')) return;
		const clone = [...this.content()];
		clone.splice(index, 1);
		this.contentChange.emit(clone);
	}

	protected moveItem(index: number, dir: -1 | 1): void {
		const clone = [...this.content()];
		const j = index + dir;
		if (j < 0 || j >= clone.length) return;
		[clone[index], clone[j]] = [clone[j], clone[index]];
		this.contentChange.emit(clone);
	}
}
